import React from 'react';
import { useCallback, useState } from 'react';
import { useClickOutside } from '../useClickOutside';
import { DropdownContext } from './DropdownContext';
import s from './styles.module.css.json';
/**
 * @example Basic example
 *
 * ```js
 * <Canvas ctx={ctx}>
 *   <Dropdown
 *     renderTrigger={({ open, onClick }) => (
 *       <Button
 *         onClick={onClick}
 *         rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
 *       >
 *         Options
 *       </Button>
 *     )}
 *   >
 *     <DropdownMenu>
 *       <DropdownOption onClick={() => {}}>Edit</DropdownOption>
 *       <DropdownOption disabled onClick={() => {}}>
 *         Duplicate
 *       </DropdownOption>
 *       <DropdownSeparator />
 *       <DropdownOption red onClick={() => {}}>
 *         Delete
 *       </DropdownOption>
 *     </DropdownMenu>
 *   </Dropdown>
 * </Canvas>;
 * ```
 *
 * @example Option actions
 *
 * ```js
 * <Canvas ctx={ctx}>
 *   <Dropdown
 *     renderTrigger={({ open, onClick }) => (
 *       <Button
 *         onClick={onClick}
 *         rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
 *       >
 *         Fields
 *       </Button>
 *     )}
 *   >
 *     <DropdownMenu>
 *       <DropdownOption>
 *         First option
 *         <DropdownOptionAction icon={<PlusIcon />} onClick={() => {}} />
 *         <DropdownOptionAction
 *           red
 *           icon={<TrashIcon />}
 *           onClick={() => {}}
 *         />
 *       </DropdownOption>
 *       <DropdownOption>
 *         Second option
 *         <DropdownOptionAction icon={<PlusIcon />} onClick={() => {}} />
 *         <DropdownOptionAction
 *           red
 *           icon={<TrashIcon />}
 *           onClick={() => {}}
 *         />
 *       </DropdownOption>
 *     </DropdownMenu>
 *   </Dropdown>
 * </Canvas>;
 * ```
 *
 * @example Option groups
 *
 * ```js
 * <Canvas ctx={ctx}>
 *   <Dropdown
 *     renderTrigger={({ open, onClick }) => (
 *       <Button
 *         onClick={onClick}
 *         rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
 *       >
 *         Fields
 *       </Button>
 *     )}
 *   >
 *     <DropdownMenu>
 *       <DropdownGroup name="Group 1">
 *         <DropdownOption>Foo</DropdownOption>
 *         <DropdownOption>Bar</DropdownOption>
 *         <DropdownOption>Qux</DropdownOption>
 *       </DropdownGroup>
 *       <DropdownGroup name="Group 2">
 *         <DropdownOption>Foo</DropdownOption>
 *         <DropdownOption>Bar</DropdownOption>
 *         <DropdownOption>Qux</DropdownOption>
 *       </DropdownGroup>
 *       <DropdownGroup name="Group 3">
 *         <DropdownOption>Foo</DropdownOption>
 *         <DropdownOption>Bar</DropdownOption>
 *         <DropdownOption>Qux</DropdownOption>
 *       </DropdownGroup>
 *     </DropdownMenu>
 *   </Dropdown>
 * </Canvas>;
 * ```
 */
export function Dropdown(_a) {
    var renderTrigger = _a.renderTrigger, children = _a.children;
    var _b = useState(false), isOpen = _b[0], setOpen = _b[1];
    var handleClickOutside = useCallback(function (event) {
        if (!event.target.closest(".".concat(s.Dropdown__menu)) && isOpen) {
            setOpen(false);
        }
    }, [setOpen, isOpen]);
    var outsideRef = useClickOutside(handleClickOutside);
    var handleClose = useCallback(function () {
        setOpen(false);
    }, [setOpen]);
    var handleToggle = useCallback(function () {
        setOpen(function (open) { return !open; });
    }, [setOpen]);
    return (React.createElement(DropdownContext.Provider, { value: { closeMenu: handleClose } },
        React.createElement("div", { className: s.Dropdown, ref: outsideRef },
            renderTrigger({ open: isOpen, onClick: handleToggle }),
            isOpen && children)));
}
//# sourceMappingURL=Dropdown.js.map